import React, { useState, useEffect } from 'react';
import { STORE_HOURS } from '../../../constants';
import { TZ } from '../../../utils/cstUtils';

/**
 * Current weekday (0=Sun … 6=Sat) and minutes-since-midnight in the store's
 * timezone (America/Chicago) — NOT the visitor's browser timezone. This keeps
 * "Open / Closes at …" correct for out-of-state visitors.
 */
const getStoreNow = () => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: TZ, weekday: 'short', hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(new Date());
  const map = {};
  parts.forEach((p) => { map[p.type] = p.value; });
  const day = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }[map.weekday] ?? new Date().getDay();
  let hour = parseInt(map.hour, 10);
  if (hour === 24) hour = 0; // some Intl impls emit 24 at midnight
  const minutes = hour * 60 + parseInt(map.minute, 10);
  return { day, minutes };
};

/**
 * Converts 'HH:MM' to minutes since midnight
 */
const timeToMinutes = (timeStr) => {
  const [h, m] = timeStr.split(':').map(Number);
  return h * 60 + m;
};

/**
 * Formats 'HH:MM' to 12-hour format
 */
const formatTime = (timeStr) => {
  const [h, m] = timeStr.split(':').map(Number);
  const suffix = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return `${hour12}:${m.toString().padStart(2, '0')} ${suffix}`;
};

/**
 * Checks if store is currently open and returns status info
 */
const getStoreStatus = () => {
  const { day, minutes: currentMinutes } = getStoreNow();

  const todayHours = STORE_HOURS[day];
  if (!todayHours || todayHours.length === 0) {
    return { isOpen: false, message: getNextOpening(day, currentMinutes) };
  }

  // Check if currently open and find closing time
  for (const slot of todayHours) {
    const openMin = timeToMinutes(slot.open);
    const closeMin = timeToMinutes(slot.close);
    if (currentMinutes >= openMin && currentMinutes < closeMin) {
      return { isOpen: true, message: `Closes at ${formatTime(slot.close)}` };
    }
  }

  return { isOpen: false, message: getNextOpening(day, currentMinutes) };
};

/**
 * Returns next opening time as a string
 */
const getNextOpening = (currentDay, currentMinutes) => {
  // Check today first for remaining slots
  const todayHours = STORE_HOURS[currentDay];
  if (todayHours) {
    for (const slot of todayHours) {
      const openMin = timeToMinutes(slot.open);
      if (currentMinutes < openMin) {
        return `Opens at ${formatTime(slot.open)}`;
      }
    }
  }

  // Check next 7 days
  for (let i = 1; i <= 7; i++) {
    const nextDay = (currentDay + i) % 7;
    const nextHours = STORE_HOURS[nextDay];
    if (nextHours && nextHours.length > 0) {
      const dayName = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][nextDay];
      return `Opens ${i === 1 ? 'tomorrow' : dayName} at ${formatTime(nextHours[0].open)}`;
    }
  }

  return 'Closed';
};

/**
 * StoreStatus — Shows open/closed indicator with next event
 * Clicking the badge scrolls to the hours section in footer
 */
const StoreStatus = () => {
  const [status, setStatus] = useState(getStoreStatus());

  // Update status every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setStatus(getStoreStatus());
    }, 60000); // 60 seconds

    return () => clearInterval(interval);
  }, []);

  // Scroll to hours section in footer
  const handleClick = () => {
    const hoursSection = document.getElementById('store-hours-section');
    if (hoursSection) {
      hoursSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="fm-store-status">
      <div
        className="fm-store-status__badge"
        onClick={handleClick}
        style={{ cursor: 'pointer' }}
        title="Click to see full hours"
      >
        <span className={`fm-store-status__dot ${status.isOpen ? 'fm-store-status__dot--open' : 'fm-store-status__dot--closed'}`} />
        <span className="fm-store-status__label">{status.message}</span>
      </div>
    </div>
  );
};

export { getStoreStatus };
export default StoreStatus;
